/**
 * Per-sheet column definitions for the Daily Photo List table.
 *
 * Mirrors backend/invi/reports/daily_photo_list_columns.py exactly.
 * Column order must match the Excel output.
 */
import type { PhotoListVehicle } from "../api/photoList"

export interface PhotoListColumn {
  /** Field key on the PhotoListVehicle object */
  key: keyof PhotoListVehicle
  /** Display header (matches EXPORT_HEADER_LABELS in backend config) */
  header: string
  /** Column width in px */
  width: number
  /** Text alignment */
  align?: "left" | "center" | "right"
  /** Use monospace font */
  mono?: boolean
}

// Default NEW columns: Lexus, Mazda, Kia
export const COLUMNS_NEW_DEFAULT: PhotoListColumn[] = [
  { key: "age_days", header: "Age", width: 50, align: "center" },
  { key: "sale_status_raw", header: "Status", width: 60, align: "center" },
  { key: "vin", header: "VIN", width: 160, mono: true },
  { key: "stock", header: "Stock", width: 100, mono: true },
  { key: "stargard", header: "Stargard", width: 120 },
  { key: "shot_status", header: "Shot Status", width: 95, align: "center" },
  { key: "year", header: "Year", width: 55, align: "center" },
  { key: "make", header: "Make", width: 80 },
  { key: "model", header: "Model", width: 140 },
  { key: "trim", header: "Trim", width: 170 },
  { key: "color_ext", header: "Color", width: 90 },
  { key: "print_notes", header: "Notes", width: 160 },
]

// Toyota NEW: adds STOP column after Status
export const COLUMNS_NEW_TOYOTA: PhotoListColumn[] = [
  { key: "age_days", header: "Age", width: 50, align: "center" },
  { key: "sale_status_raw", header: "Status", width: 60, align: "center" },
  { key: "cmpgn", header: "STOP", width: 55, align: "center" },
  { key: "vin", header: "VIN", width: 160, mono: true },
  { key: "stock", header: "Stock", width: 100, mono: true },
  { key: "stargard", header: "Stargard", width: 120 },
  { key: "shot_status", header: "Shot Status", width: 95, align: "center" },
  { key: "year", header: "Year", width: 55, align: "center" },
  { key: "make", header: "Make", width: 80 },
  { key: "model", header: "Model", width: 140 },
  { key: "trim", header: "Trim", width: 170 },
  { key: "color_ext", header: "Color", width: 90 },
  { key: "print_notes", header: "Notes", width: 160 },
]

// Dodge/Fernley NEW: adds Transit column after Status
export const COLUMNS_NEW_DODGE_FERNLEY: PhotoListColumn[] = [
  { key: "age_days", header: "Age", width: 50, align: "center" },
  { key: "sale_status_raw", header: "Status", width: 60, align: "center" },
  { key: "transit", header: "Transit", width: 110, align: "center" },
  { key: "vin", header: "VIN", width: 160, mono: true },
  { key: "stock", header: "Stock", width: 100, mono: true },
  { key: "stargard", header: "Stargard", width: 120 },
  { key: "shot_status", header: "Shot Status", width: 95, align: "center" },
  { key: "year", header: "Year", width: 55, align: "center" },
  { key: "make", header: "Make", width: 80 },
  { key: "model", header: "Model", width: 140 },
  { key: "trim", header: "Trim", width: 170 },
  { key: "color_ext", header: "Color", width: 90 },
  { key: "print_notes", header: "Notes", width: 160 },
]

// All USED sheets
export const COLUMNS_USED: PhotoListColumn[] = [
  { key: "age_days", header: "Age", width: 50, align: "center" },
  { key: "recon_status_raw", header: "Rapid Recon", width: 140 },
  { key: "vin", header: "VIN", width: 160, mono: true },
  { key: "stock", header: "Stock", width: 100, mono: true },
  { key: "stargard", header: "Stargard", width: 120 },
  { key: "shot_status", header: "Shot Status", width: 95, align: "center" },
  { key: "year", header: "Year", width: 55, align: "center" },
  { key: "make", header: "Make", width: 80 },
  { key: "model", header: "Model", width: 140 },
  { key: "trim", header: "Trim", width: 170 },
  { key: "color_ext", header: "Color", width: 90 },
  { key: "print_notes", header: "Notes", width: 160 },
]

// The Reshoots sheet (Ian, 2026-11-03): every store's cars that need to be
// shot again, so the store is a column. Mirrors DAILY_LIST_COLUMNS_RESHOOTS.
export const COLUMNS_RESHOOTS: PhotoListColumn[] = [
  { key: "rooftop", header: "Store", width: 110 },
  { key: "vin", header: "VIN", width: 160, mono: true },
  { key: "stock", header: "Stock", width: 100, mono: true },
  { key: "stargard", header: "Stargard", width: 120 },
  { key: "year", header: "Year", width: 55, align: "center" },
  { key: "make", header: "Make", width: 80 },
  { key: "model", header: "Model", width: 140 },
  { key: "trim", header: "Trim", width: 170 },
  { key: "color_ext", header: "Color", width: 90 },
  { key: "print_notes", header: "Notes", width: 160 },
]

/** Mirrors RESHOOTS_SHEET_SLUG in backend/invi/config/daily_photo_list_sheets.py
 * (also exported by context/PhotoListContext.tsx; kept here so this config
 * module imports nothing from the context). */
const RESHOOTS_SHEET_SLUG = "reshoots"

/** What the Notes column says for a car added to the list by hand when it has
 * no note of its own (Ian, 2026-10-11) — print view here, Excel on the server.
 * Mirrors HAND_ADDED_NOTES_MARK in backend/invi/config/daily_photo_list_config.py. */
export const HAND_ADDED_NOTES_MARK = "ADDED"
/** Its twin for a car on the Reshoots sheet with no note of its own. Mirrors
 * RESHOOT_NOTES_MARK in backend/invi/config/daily_photo_list_config.py. */
export const RESHOOT_NOTES_MARK = "RESHOOT"

export function getColumnsForSheet(sheetSlug: string): PhotoListColumn[] {
  if (sheetSlug === RESHOOTS_SHEET_SLUG) return COLUMNS_RESHOOTS
  if (sheetSlug === "toyota_new") return COLUMNS_NEW_TOYOTA
  if (sheetSlug === "dodge_new" || sheetSlug === "fernley_new")
    return COLUMNS_NEW_DODGE_FERNLEY
  if (sheetSlug.endsWith("_new")) return COLUMNS_NEW_DEFAULT
  return COLUMNS_USED
}
